import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { useBusiness, useBusinessInUrl } from "@/core/business";
import { ApTabList } from "./ap-tab-list";
import { ApAutomatedTab } from "./automated-tab";
import { BulkApproveButton } from "./bulk-approve-button";
import { ApEmailDetail, ApEmailsTab } from "./emails-tab";
import { ApInvoiceDetail } from "./invoice-detail";
import { ApInvoicesTab } from "./invoices-tab";
import {
  QuickbooksReconnectBanner,
  QuickbooksView,
  useBillSettingsDraft,
} from "./quickbooks";
import { ApReceiptDetail } from "./receipt-detail";
import { ApReceiptsTab } from "./receipts-tab";
import { ApCombinedInvoicesTab } from "./review-tab";
import { UploadInvoicesButton } from "./upload-invoices-button";
import { useApTabRoute } from "./use-ap-tab-route";
import { useBulkApprove } from "./use-bulk-approve";
import { useHighConfidenceQueue } from "./use-high-confidence-queue";
import {
  AP_TAB,
  type ApTabValue,
  invoiceReviewPath,
  receiptReviewPath,
  apListPath,
} from "./utils";
import { ApVendorDetail, ApVendorsTab } from "./vendors-tab";

const SELECTED_OBJECT_KIND = {
  email: "email",
  invoice: "invoice",
  receipt: "receipt",
  vendor: "vendor",
} as const;

type SelectedObjectKind =
  (typeof SELECTED_OBJECT_KIND)[keyof typeof SELECTED_OBJECT_KIND];

type Selection = { kind: SelectedObjectKind; id: string } | null;

const selectedIdFor = (
  selected: Selection,
  kind: SelectedObjectKind,
): string | null => (selected?.kind === kind ? selected.id : null);

const AccountsPayable = () => {
  const { businessId } = useBusiness();
  useBusinessInUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState<Selection>(null);
  // Held here rather than in the QuickBooks view, so unsaved bill settings
  // survive a look at another view (say Vendors, from "Review vendors").
  const quickbooksSettings = useBillSettingsDraft(businessId);

  const onReviewTab =
    location.pathname === apListPath(AP_TAB.review) ||
    (location.pathname === apListPath() &&
      (new URLSearchParams(location.search).get("tab") ?? AP_TAB.review) ===
        AP_TAB.review);
  const highConfidence = useHighConfidenceQueue(
    onReviewTab ? businessId : null,
  );
  const { approvingIds: bulkApprovingIds, approve: approveIds } =
    useBulkApprove(businessId);
  const highConfidenceIds = highConfidence.actionIds;

  const { activeTab, goToTab } = useApTabRoute();
  const [previousTab, setPreviousTab] = useState(activeTab);
  if (previousTab !== activeTab) {
    setPreviousTab(activeTab);
    setSelected(null);
  }

  const closeSidebar = () => setSelected(null);
  const changeTab = (v: ApTabValue) => goToTab(v);
  const selectObject = (kind: SelectedObjectKind, id: string) =>
    setSelected({ kind, id });
  const openInvoiceReview = (id: string) =>
    navigate(invoiceReviewPath(id, activeTab, location.search), {
      state: {
        invoiceListReturnContext: {
          businessId,
          listPath: location.pathname + location.search + location.hash,
        },
      },
    });
  const openReceiptReview = (id: string) =>
    navigate(receiptReviewPath(id, activeTab, location.search));

  return (
    <SimpleSidebarLayout
      open={!!selected}
      onOpenChange={(open) => !open && setSelected(null)}
    >
      <SimpleSidebarContent removeScrollArea>
        {/* A workspace page: exactly the page area's height, with each tab's
            list scrolling inside it. */}
        <PageContainer variant="workspace">
          {/* The QuickBooks view is settings, not a list: it shows a
              reconnect in its own card, and has no upload or approve. */}
          {activeTab !== AP_TAB.quickbooks && <QuickbooksReconnectBanner />}
          <PageHeader
            title="Accounts payable"
            actions={
              activeTab !== AP_TAB.quickbooks && (
                <>
                  {activeTab === AP_TAB.review &&
                    highConfidenceIds.length > 0 && (
                      <BulkApproveButton
                        actionIds={highConfidenceIds}
                        partial={highConfidence.partial}
                        scope={highConfidence.scope}
                        disabled={!highConfidence.current}
                        pending={highConfidenceIds.some((id) =>
                          bulkApprovingIds.has(id),
                        )}
                        onApprove={approveIds}
                      />
                    )}
                  <UploadInvoicesButton
                    businessId={businessId}
                    // Receipts are uploaded from their own tab; everywhere else
                    // the button uploads invoices, as before.
                    documentType={
                      activeTab === AP_TAB.receipts ? "receipt" : "invoice"
                    }
                  />
                </>
              )
            }
          />

          <ApTabList
            businessId={businessId}
            activeTab={activeTab}
            onTabChange={changeTab}
          />

          <Stack gap={0} className="min-h-0 flex-1">
            {/* Review and Automated stack one card per invoice on the page
                  ground. Invoices and Receipts keep the page filter row
                  above the table, as on main; Invoices puts Action and
                  Assignee in the table frame. */}
            {activeTab === AP_TAB.review ? (
              <Stack
                align="start"
                gap={0}
                className="min-h-0 w-full flex-1 overflow-hidden"
              >
                <ApCombinedInvoicesTab
                  key={businessId}
                  businessId={businessId}
                  bulkApprovingIds={bulkApprovingIds}
                  onBulkApprove={(ids) =>
                    void approveIds(ids).catch(() =>
                      toast.error(
                        "Couldn't approve the actions. Please try again.",
                      ),
                    )
                  }
                  onBrowseInvoices={() => changeTab(AP_TAB.invoices)}
                />
              </Stack>
            ) : activeTab === AP_TAB.automated ? (
              <Stack
                align="start"
                gap={0}
                className="min-h-0 w-full flex-1 overflow-hidden"
              >
                <ApAutomatedTab
                  businessId={businessId}
                  onSelectInvoice={openInvoiceReview}
                />
              </Stack>
            ) : activeTab === AP_TAB.invoices ? (
              // No overflow clip here: it would cut off the filter controls'
              // focus rings. The table card clips its own content.
              <Stack align="start" gap={0} className="min-h-0 w-full flex-1">
                <ApInvoicesTab
                  key={businessId}
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.invoice,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.invoice, id)
                  }
                />
              </Stack>
            ) : activeTab === AP_TAB.receipts ? (
              <Stack align="start" gap={0} className="min-h-0 w-full flex-1">
                <ApReceiptsTab
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.receipt,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.receipt, id)
                  }
                />
              </Stack>
            ) : activeTab === AP_TAB.emails ? (
              <Stack align="start" gap={0} className="min-h-0 w-full flex-1">
                <ApEmailsTab
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.email,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.email, id)
                  }
                />
              </Stack>
            ) : activeTab === AP_TAB.quickbooks ? (
              <Stack align="start" gap={0} className="min-h-0 w-full flex-1">
                <QuickbooksView
                  settings={quickbooksSettings}
                  onReviewVendors={() => changeTab(AP_TAB.vendors)}
                />
              </Stack>
            ) : (
              <Stack align="start" gap={0} className="min-h-0 w-full flex-1">
                <ApVendorsTab
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.vendor,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.vendor, id)
                  }
                />
              </Stack>
            )}
          </Stack>
        </PageContainer>
      </SimpleSidebarContent>

      <SimpleSidebar>
        {selected?.kind === SELECTED_OBJECT_KIND.email && (
          <ApEmailDetail
            emailId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
          />
        )}
        {selected?.kind === SELECTED_OBJECT_KIND.vendor && (
          <ApVendorDetail
            vendorId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
          />
        )}
        {selected?.kind === SELECTED_OBJECT_KIND.receipt && (
          <ApReceiptDetail
            receiptId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
            onViewAgentReview={(id) => {
              setSelected(null);
              void openReceiptReview(id);
            }}
          />
        )}
        {selected?.kind === SELECTED_OBJECT_KIND.invoice && (
          <ApInvoiceDetail
            invoiceId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
            onViewAgentReview={(id) => {
              setSelected(null);
              void openInvoiceReview(id);
            }}
          />
        )}
      </SimpleSidebar>
    </SimpleSidebarLayout>
  );
};

export default AccountsPayable;
